import { useMemo, useRef, useState } from 'react';
import { Search, Check, Layers, ClipboardCheck, Settings2, ImageOff } from 'lucide-react';
import { getAllCategories, getCategory, normalize } from '../../data/catalog';
import { getUser } from '../../data/users';
import { useQueue } from '../../context/QueueContext';
import { VARIANT_STATUS, VARIANT_STATUS_LABELS, useAvailabilityTick } from '../../utils/availability';
import {
  getReviewDays, setReviewDays, listReviewItems,
  confirmAvailable, reviewMarkOutOfStock, postponeReview,
  backInStock, stillOutOfStock, markNoRestock,
} from '../../utils/review';

const SIT_FILTERS = [
  { key: 'todos', label: 'Todos' },
  { key: 'PENDENTE', label: 'Pendentes' },
  { key: 'ATRASADA', label: 'Atrasados' },
  { key: 'ADIADA', label: 'Adiados' },
];

const SIT_BADGE = {
  PENDENTE: 'bg-amber-100 text-amber-800',
  ATRASADA: 'bg-red-100 text-red-700',
  ADIADA: 'bg-sky-100 text-sky-800',
};
const SIT_LABEL = { PENDENTE: 'Pendente', ATRASADA: 'Atrasada', ADIADA: 'Adiada' };

const chipCls = (active) =>
  `shrink-0 rounded-full border px-4 py-2.5 text-xs font-semibold transition-colors ${
    active ? 'border-navy bg-navy text-white' : 'border-navy/20 text-foreground/70 hover:border-navy'
  }`;

export default function AdminReview() {
  const { user } = useQueue();
  const tick = useAvailabilityTick();
  const isAdmin = user.role === 'admin';

  const [q, setQ] = useState('');
  const [sitFilter, setSitFilter] = useState('todos');
  const [catFilter, setCatFilter] = useState('todas');
  const [ageFilter, setAgeFilter] = useState('todas');
  const [batchMode, setBatchMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [batchConfirmOpen, setBatchConfirmOpen] = useState(false);
  const [confirmNoRestock, setConfirmNoRestock] = useState(null);
  const [flash, setFlash] = useState(null);
  const listRef = useRef(null);

  const prazo = getReviewDays();
  const items = useMemo(() => listReviewItems(), [tick]);
  const reviewable = items.filter((i) => i.situation && i.situation !== 'EM_DIA');
  const outOfStock = items.filter((i) => i.status === VARIANT_STATUS.TEMPORARIAMENTE_INDISPONIVEL);

  const counts = {
    pendentes: reviewable.filter((i) => i.situation === 'PENDENTE').length,
    atrasados: reviewable.filter((i) => i.situation === 'ATRASADA').length,
    adiados: reviewable.filter((i) => i.situation === 'ADIADA').length,
    indisponiveis: outOfStock.length,
  };

  const ageBuckets = [
    { key: 'todas', label: 'Qualquer tempo' },
    { key: 'b1', label: `${prazo}–${prazo + 29} dias`, test: (d) => d >= prazo && d < prazo + 30 },
    { key: 'b2', label: `${prazo + 30}–${prazo + 59} dias`, test: (d) => d >= prazo + 30 && d < prazo + 60 },
    { key: 'b3', label: `${prazo + 60}+ dias`, test: (d) => d >= prazo + 60 },
  ];
  const activeBucket = ageBuckets.find((b) => b.key === ageFilter);

  const results = useMemo(() => {
    const tokens = normalize(q).split(/\s+/).filter(Boolean);
    return reviewable
      .filter((i) => {
        if (sitFilter !== 'todos' && i.situation !== sitFilter) return false;
        if (catFilter !== 'todas' && i.product.categorySlug !== catFilter) return false;
        if (activeBucket?.test && !activeBucket.test(i.days)) return false;
        if (!tokens.length) return true;
        const cat = getCategory(i.product.categorySlug);
        const hay = normalize([i.product.name, i.variant?.name || '', cat?.name, i.code, ...(i.product.colorTags || []), ...(i.product.keywords || [])].join(' '));
        return tokens.every((t) => hay.includes(t));
      })
      .sort((a, b) => b.days - a.days);
  }, [reviewable, q, sitFilter, catFilter, activeBucket]);

  const catCounts = useMemo(() => {
    const map = {};
    for (const i of reviewable) map[i.product.categorySlug] = (map[i.product.categorySlug] || 0) + 1;
    return map;
  }, [reviewable]);

  const flashMsg = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), 3000);
  };

  const toggleSelect = (code) =>
    setSelected((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  const runBatch = () => {
    for (const code of selected) {
      const entry = reviewable.find((e) => e.code === code);
      if (entry) confirmAvailable(entry.product, entry.variant, user);
    }
    setSelected([]);
    setBatchMode(false);
    setBatchConfirmOpen(false);
    flashMsg('Disponibilidades confirmadas.');
  };

  return (
    <div className={batchMode && selected.length > 0 ? 'pb-44' : 'pb-16'} data-testid="review-page">
      <h2 className="font-serif text-2xl text-navy">Revisão de Catálogo</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Variantes marcadas como disponíveis há mais de {prazo} dias sem confirmação entram aqui.
        Isso não significa que acabaram — significa: "precisamos conferir este item".
      </p>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="review-dashboard">
        {[
          ['Itens pendentes', counts.pendentes, 'review-stat-pendentes'],
          ['Atrasados', counts.atrasados, 'review-stat-atrasados'],
          ['Adiados', counts.adiados, 'review-stat-adiados'],
          ['Indisponíveis para revisar', counts.indisponiveis, 'review-stat-indisponiveis'],
        ].map(([label, value, testid]) => (
          <div key={testid} className="rounded-xl border border-navy/10 bg-white p-4" data-testid={testid}>
            <p className="font-serif text-2xl text-navy">{value}</p>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => { setSitFilter('todos'); listRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
          data-testid="review-start-button"
          className="flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-hover"
        >
          <ClipboardCheck className="h-4 w-4" /> INICIAR REVISÃO
        </button>
      </div>

      {isAdmin && (
        <div className="mt-5 rounded-2xl border border-navy/10 bg-white p-5" data-testid="review-config">
          <p className="flex items-center gap-2 text-sm font-semibold text-navy">
            <Settings2 className="h-4 w-4" /> Configurações do catálogo (somente administrador)
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Revisar disponibilidade após:</p>
          <div className="mt-2 flex gap-2">
            {[30, 60, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setReviewDays(d)}
                data-testid={`review-config-days-${d}`}
                aria-pressed={prazo === d}
                className={chipCls(prazo === d)}
              >
                {d} dias
              </button>
            ))}
          </div>
        </div>
      )}

      <div ref={listRef} className="mt-8">
        <form onSubmit={(e) => e.preventDefault()} role="search">
          <label htmlFor="review-search" className="sr-only">Buscar tecido, variante ou código</label>
          <div className="flex w-full">
            <input
              id="review-search"
              data-testid="review-search-input"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar tecido, variante ou código"
              className="h-14 w-full rounded-l-2xl border border-r-0 border-input bg-white px-5 text-base outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
            />
            <span className="flex h-14 items-center rounded-r-2xl border border-l-0 border-input bg-white px-4 text-navy">
              <Search className="h-5 w-5" />
            </span>
          </div>
        </form>

        <div className="no-scrollbar -mx-4 mt-4 flex gap-2 overflow-x-auto px-4" data-testid="review-situation-filters">
          {SIT_FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setSitFilter(f.key)} data-testid={`review-filter-${f.key.toLowerCase()}`} aria-pressed={sitFilter === f.key} className={chipCls(sitFilter === f.key)}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <label htmlFor="review-category" className="sr-only">Filtrar por categoria</label>
            <select
              id="review-category"
              data-testid="review-category-select"
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              className="h-12 w-full rounded-xl border border-input bg-white px-4 text-sm font-medium outline-none focus:border-navy"
            >
              <option value="todas">Categoria: Todas ({reviewable.length} pendentes)</option>
              {getAllCategories().map((c) => (
                <option key={c.slug} value={c.slug}>{c.name} — {catCounts[c.slug] || 0} pendentes</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="review-age" className="sr-only">Filtrar por tempo sem revisão</label>
            <select
              id="review-age"
              data-testid="review-age-select"
              value={ageFilter}
              onChange={(e) => setAgeFilter(e.target.value)}
              className="h-12 w-full rounded-xl border border-input bg-white px-4 text-sm font-medium outline-none focus:border-navy"
            >
              {ageBuckets.map((b) => <option key={b.key} value={b.key}>{b.key === 'todas' ? 'Tempo sem revisão: qualquer' : `${b.label} sem revisão`}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground" data-testid="review-result-count">
            {results.length} {results.length === 1 ? 'item' : 'itens'} · mais antigos primeiro
          </p>
          <button
            type="button"
            onClick={() => { setBatchMode((b) => !b); setSelected([]); }}
            data-testid="review-batch-toggle"
            aria-pressed={batchMode}
            className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-xs font-semibold ${batchMode ? 'border-navy bg-navy text-white' : 'border-navy/20 text-navy hover:bg-navy-light'}`}
          >
            <Layers className="h-4 w-4" /> {batchMode ? 'SAIR DA SELEÇÃO' : 'SELECIONAR VÁRIAS'}
          </button>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="review-list">
          {results.map((entry) => {
            const cat = getCategory(entry.product.categorySlug);
            const isSelected = selected.includes(entry.code);
            const confirmedBy = entry.meta.lastConfirmedBy ? getUser(entry.meta.lastConfirmedBy)?.name : null;
            const vId = entry.variant?.id || 'produto';
            const vName = entry.variant?.name || null;
            const imgSrc = entry.variant?.image || entry.product.coverImage || entry.product.image || null;
            return (
              <div
                key={entry.code}
                className={`rounded-2xl border bg-white p-3 transition-colors ${isSelected ? 'border-navy ring-2 ring-navy/30' : 'border-navy/10'}`}
                data-testid={`review-card-${entry.product.slug}-${vId}`}
              >
                <div className="flex gap-3">
                  {batchMode && (
                    <button
                      type="button"
                      onClick={() => toggleSelect(entry.code)}
                      data-testid={`review-select-${entry.product.slug}-${vId}`}
                      aria-pressed={isSelected}
                      aria-label={`Selecionar ${entry.product.name}${vName ? ` ${vName}` : ''}`}
                      className={`flex h-7 w-7 shrink-0 items-center justify-center self-start rounded-lg border-2 ${isSelected ? 'border-navy bg-navy text-white' : 'border-navy/25 bg-white'}`}
                    >
                      {isSelected && <Check className="h-4 w-4" />}
                    </button>
                  )}
                  {imgSrc ? (
                    <img src={imgSrc} alt={vName ? `${entry.product.name} - ${vName}` : entry.product.name} loading="lazy" className="h-28 w-28 shrink-0 rounded-xl object-cover sm:h-24 sm:w-24" />
                  ) : (
                    <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-xl bg-navy-light sm:h-24 sm:w-24" data-testid={`review-photo-empty-${entry.product.slug}-${vId}`}>
                      <ImageOff className="h-6 w-6 text-navy/40" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{cat?.name}</p>
                    <p className="truncate font-medium leading-tight">{entry.product.name}</p>
                    {vName ? (
                      <p className="truncate text-sm text-muted-foreground">{vName}</p>
                    ) : (
                      <p className="truncate text-sm font-medium text-navy/70" data-testid={`review-unit-${entry.product.slug}`}>Produto sem variantes</p>
                    )}
                    <p className="mt-0.5 truncate text-[10px] text-muted-foreground/70">Cód: {entry.variant ? entry.code : entry.product.slug}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${SIT_BADGE[entry.situation]}`} data-testid={`review-situation-${entry.product.slug}-${vId}`}>
                        {SIT_LABEL[entry.situation]}
                      </span>
                      {isAdmin && entry.meta.postponements >= 2 && (
                        <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-[11px] font-semibold text-red-700" data-testid={`review-postponed-alert-${entry.product.slug}-${vId}`}>
                          Revisão adiada repetidamente
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Última confirmação: há {entry.days} dias{confirmedBy ? ` · por ${confirmedBy}` : ''}
                    </p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => { confirmAvailable(entry.product, entry.variant, user); flashMsg('Disponibilidade confirmada.'); }}
                    data-testid={`review-action-available-${entry.product.slug}-${vId}`}
                    className="min-h-11 rounded-xl border border-emerald-500/50 px-2 py-2.5 text-[11px] font-bold tracking-wide text-emerald-700 transition-colors hover:bg-emerald-50"
                  >
                    CONTINUA DISPONÍVEL
                  </button>
                  <button
                    type="button"
                    onClick={() => { reviewMarkOutOfStock(entry.product, entry.variant, user); flashMsg('Marcado como sem estoque.'); }}
                    data-testid={`review-action-out-${entry.product.slug}-${vId}`}
                    className="min-h-11 rounded-xl border border-amber-400/60 px-2 py-2.5 text-[11px] font-bold tracking-wide text-amber-700 transition-colors hover:bg-amber-50"
                  >
                    SEM ESTOQUE
                  </button>
                  <button
                    type="button"
                    onClick={() => { postponeReview(entry.product, entry.variant, user); flashMsg('Revisão adiada.'); }}
                    data-testid={`review-action-later-${entry.product.slug}-${vId}`}
                    className="min-h-11 rounded-xl border border-navy/20 px-2 py-2.5 text-[11px] font-bold tracking-wide text-navy transition-colors hover:bg-navy-light"
                  >
                    REVISAR DEPOIS
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {results.length === 0 && (
          <p className="mt-8 rounded-2xl border border-dashed border-navy/20 bg-white p-10 text-center text-sm text-muted-foreground" data-testid="review-empty">
            Nenhum item pendente para os filtros atuais. Catálogo em dia.
          </p>
        )}
      </div>

      <section className="mt-10" data-testid="oos-section">
        <h3 className="font-serif text-xl text-navy">Indisponíveis para revisar</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Variantes sem estoque — verifique se retornaram à loja.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {outOfStock.map((entry) => {
            const cat = getCategory(entry.product.categorySlug);
            const vId = entry.variant?.id || 'produto';
            const vName = entry.variant?.name || null;
            const imgSrc = entry.variant?.image || entry.product.coverImage || entry.product.image || null;
            return (
              <div key={entry.code} className="rounded-2xl border border-navy/10 bg-white p-3" data-testid={`oos-card-${entry.product.slug}-${vId}`}>
                <div className="flex gap-3">
                  {imgSrc ? (
                    <img src={imgSrc} alt={vName ? `${entry.product.name} - ${vName}` : entry.product.name} loading="lazy" className="h-28 w-28 shrink-0 rounded-xl object-cover sm:h-24 sm:w-24" />
                  ) : (
                    <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-xl bg-navy-light sm:h-24 sm:w-24">
                      <ImageOff className="h-6 w-6 text-navy/40" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{cat?.name}</p>
                    <p className="truncate font-medium leading-tight">{entry.product.name}</p>
                    {vName ? (
                      <p className="truncate text-sm text-muted-foreground">{vName}</p>
                    ) : (
                      <p className="truncate text-sm font-medium text-navy/70" data-testid={`oos-unit-${entry.product.slug}`}>Produto sem variantes</p>
                    )}
                    <span className="mt-1.5 inline-block rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">Sem estoque</span>
                    {entry.outOfStockDays !== null && (
                      <p className="mt-1 text-xs text-muted-foreground">Sem estoque há {entry.outOfStockDays} dias</p>
                    )}
                  </div>
                </div>
                <div className={`mt-3 grid gap-2 ${isAdmin ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  <button
                    type="button"
                    onClick={() => { backInStock(entry.product, entry.variant, user); flashMsg('Voltou ao estoque.'); }}
                    data-testid={`oos-action-back-${entry.product.slug}-${vId}`}
                    className="min-h-11 rounded-xl border border-emerald-500/50 px-2 py-2.5 text-[11px] font-bold tracking-wide text-emerald-700 hover:bg-emerald-50"
                  >
                    VOLTOU AO ESTOQUE
                  </button>
                  <button
                    type="button"
                    onClick={() => { stillOutOfStock(entry.product, entry.variant, user); flashMsg('Revisão registrada — continua sem estoque.'); }}
                    data-testid={`oos-action-still-${entry.product.slug}-${vId}`}
                    className="min-h-11 rounded-xl border border-navy/20 px-2 py-2.5 text-[11px] font-bold tracking-wide text-navy hover:bg-navy-light"
                  >
                    CONTINUA SEM ESTOQUE
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => setConfirmNoRestock(entry)}
                      data-testid={`oos-action-norestock-${entry.product.slug}-${vId}`}
                      className="min-h-11 rounded-xl border border-red-300 px-2 py-2.5 text-[11px] font-bold tracking-wide text-red-700 hover:bg-red-50"
                    >
                      SEM PREVISÃO
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {outOfStock.length === 0 && (
            <p className="rounded-2xl border border-dashed border-navy/20 bg-white p-8 text-center text-sm text-muted-foreground sm:col-span-2 xl:col-span-3" data-testid="oos-empty">
              Nenhum item sem estoque no momento.
            </p>
          )}
        </div>
      </section>

      {flash && (
        <div className="fixed inset-x-0 bottom-4 z-[75] flex justify-center px-4" data-testid="review-toast">
          <p className="rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white shadow-lg">{flash}</p>
        </div>
      )}

      {batchMode && selected.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-[74] border-t border-navy/10 bg-white px-4 py-3 shadow-[0_-4px_20px_rgba(15,41,66,0.12)]" data-testid="review-batch-bar">
          <p className="text-center text-xs font-semibold text-navy">{selected.length} selecionadas</p>
          <div className="mx-auto mt-2 flex max-w-lg justify-center">
            <button
              type="button"
              onClick={() => setBatchConfirmOpen(true)}
              data-testid="review-batch-confirm-available"
              className="rounded-full bg-navy px-6 py-2.5 text-xs font-bold text-white hover:bg-navy-hover"
            >
              CONFIRMAR COMO DISPONÍVEIS
            </button>
          </div>
        </div>
      )}

      {(batchConfirmOpen || confirmNoRestock) && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 p-4 sm:items-center" role="dialog" aria-modal="true" data-testid="review-confirm-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            {batchConfirmOpen ? (
              <>
                <p className="font-serif text-lg text-navy">Confirmar verificação física</p>
                <p className="mt-2 text-sm text-muted-foreground" data-testid="review-batch-confirm-text">
                  Você confirma que verificou fisicamente estas {selected.length} {selected.length === 1 ? 'variante' : 'variantes'}?
                </p>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setBatchConfirmOpen(false)} data-testid="review-batch-cancel" className="rounded-full border border-navy/20 py-3 text-sm font-semibold text-navy hover:bg-navy-light">CANCELAR</button>
                  <button type="button" onClick={runBatch} data-testid="review-batch-confirm" className="rounded-full bg-navy py-3 text-sm font-semibold text-white hover:bg-navy-hover">CONFIRMAR</button>
                </div>
              </>
            ) : (
              <>
                <p className="font-serif text-lg text-navy">Confirmar “Sem previsão de reposição”</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  <strong>{confirmNoRestock.product.name}{confirmNoRestock.variant ? ` — ${confirmNoRestock.variant.name}` : ''}</strong> será arquivado e
                  ficará fora do catálogo público. O cadastro, fotos e histórico serão preservados.
                </p>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setConfirmNoRestock(null)} data-testid="norestock-cancel" className="rounded-full border border-navy/20 py-3 text-sm font-semibold text-navy hover:bg-navy-light">CANCELAR</button>
                  <button
                    type="button"
                    onClick={() => { markNoRestock(confirmNoRestock.product, confirmNoRestock.variant, user); setConfirmNoRestock(null); flashMsg('Variante arquivada (sem previsão).'); }}
                    data-testid="norestock-confirm"
                    className="rounded-full bg-red-600 py-3 text-sm font-semibold text-white hover:bg-red-700"
                  >
                    CONFIRMAR
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
